import React, { useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.css";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { useLocation, NavLink } from "react-router-dom";
import { getSectionName , checkMenuActive, isSectionActive, isAnyDescendantActive } from "../../../../../Utils/utils";
import {ACTIVE_CLASS } from "../../../../../Utils/Constants";
import useFlyoutPanel, { FLYOUT_PANEL_ATTRIBUTE, FLYOUT_PLACEMENT_RIGHT } from "../../../../../Utils/Hooks/useFlyoutPanel";
import FlyoutScrollHint from "../../../../../Components/common/FlyoutScrollHint";
import useFlyoutTrigger from "../../../../../Utils/Hooks/useFlyoutTrigger";
import useIsTouchInput from "../../../../../Utils/Hooks/useIsTouchInput";

const SubMenuItem = ({ item, changeSection, getIconSection, isTouchInput, onNavigate }) => {
  const location = useLocation();
  const hasChildren = item.children?.length > 0;
  const itemRef = useRef(null);
  const nestedPanelRef = useRef(null);
  const { isOpen, togglePanel, hoverProps } = useFlyoutTrigger(itemRef, isTouchInput);
  const { style: nestedPanelStyle, hasMoreBelow } = useFlyoutPanel(itemRef, nestedPanelRef, hasChildren && isOpen, FLYOUT_PLACEMENT_RIGHT);

  const handleNavigate = () => {
    changeSection(item.section);
    // El submenú se recoge al elegir destino: a dedo no hay salida del ratón que lo cierre
    onNavigate();
  };

  return (
    <div ref={itemRef} className="SubMenuPanelItem" {...hoverProps}>
      {hasChildren ? (
        <div
          className={`SubMenuSection SubMenuSection--parent ${(isSectionActive(item, location.pathname) || isAnyDescendantActive(item, location.pathname)) ? ACTIVE_CLASS : ""}`}
          onClick={isTouchInput ? togglePanel : undefined}
        >
          {getIconSection(item.section?.iconsvg || item.section.iconId, item.section)}
          <span className="SubMenuSectionText">{getSectionName(item.section)}</span>
          <ion-icon name="chevron-forward-outline" className="SubMenuChevron" aria-hidden="true" />
        </div>
      ) : (
        <NavLink
          to={{ pathname: item.url, aboutProps: { dinamic: item.section.screenId } }}
          onClick={handleNavigate}
          className={`SubMenuSection ${isSectionActive(item, location.pathname) ? ACTIVE_CLASS : ""}`}
        >
          {getIconSection(item.section?.iconsvg || item.section.iconId, item.section)}
          <span className="SubMenuSectionText">{getSectionName(item.section)}</span>
          <span className="SubMenuChevronSpacer" aria-hidden="true" />
        </NavLink>
      )}
      {hasChildren && isOpen && (
        <div className="SubMenuNestedPanel" {...FLYOUT_PANEL_ATTRIBUTE} ref={nestedPanelRef} style={nestedPanelStyle}>
          <SubMenuItems
            items={item.children}
            changeSection={changeSection}
            getIconSection={getIconSection}
            isTouchInput={isTouchInput}
            onNavigate={onNavigate}
          />
          {hasMoreBelow && <FlyoutScrollHint />}
        </div>
      )}
    </div>
  );
};

const SubMenuItems = ({ items, changeSection, getIconSection, isTouchInput, onNavigate }) => (
  <>
    {items.map((child, index) => (
      <SubMenuItem
        key={child.section.name + "-" + index}
        item={child}
        changeSection={changeSection}
        getIconSection={getIconSection}
        isTouchInput={isTouchInput}
        onNavigate={onNavigate}
      />
    ))}
  </>
);

const SubMenuPanel = ({ items, changeSection, getIconSection, style, panelRef, hasMoreBelow, isTouchInput, onNavigate }) => (
  <div className="SubMenuPanel" {...FLYOUT_PANEL_ATTRIBUTE} ref={panelRef} style={style}>
    <SubMenuItems
      items={items}
      changeSection={changeSection}
      getIconSection={getIconSection}
      isTouchInput={isTouchInput}
      onNavigate={onNavigate}
    />
    {hasMoreBelow && <FlyoutScrollHint />}
  </div>
);

const MenuSectionItem = ({ section, changeSection, getIconSection, sections, sectionSelected, isTouchInput }) => {
  const location = useLocation();
  const wrapperRef = useRef(null);
  const panelRef = useRef(null);
  const hasChildren = section.children?.length > 0;
  const { isOpen, closePanel, togglePanel, hoverProps } = useFlyoutTrigger(wrapperRef, isTouchInput);
  const { style: panelStyle, hasMoreBelow } = useFlyoutPanel(wrapperRef, panelRef, hasChildren && isOpen, FLYOUT_PLACEMENT_RIGHT);

  const handleSectionClick = () => {
    changeSection(section.section);
    closePanel();
  };

  const sectionIcon = (
    <OverlayTrigger
      delay={{ hide: 450, show: 300 }}
      placement="bottom"
      overlay={(props) => <Tooltip {...props}>{getSectionName(section.section)}</Tooltip>}
    >
      {getIconSection(section.section?.iconsvg || section.section.iconId, section.section)}
    </OverlayTrigger>
  );

  return (
    <div ref={wrapperRef} {...hoverProps}>
      {hasChildren ? (
        // Una sección con hijos solo despliega su submenú: el destino está en los hijos
        <div
          className={`MenuSection MenuSection--parent ${section.section.iconId} ${(isSectionActive(section, location.pathname) || isAnyDescendantActive(section, location.pathname)) ? ACTIVE_CLASS : ""}`}
          aria-label={getSectionName(section.section)}
          onClick={isTouchInput ? togglePanel : undefined}
        >
          {sectionIcon}
        </div>
      ) : (
        <NavLink
          to={{ pathname: section.url, aboutProps: { dinamic: section.section.screenId } }}
          onClick={handleSectionClick}
          className={`MenuSection ${section.section.iconId} ${isSectionActive(section, location.pathname) ? ACTIVE_CLASS : ""}`}
          aria-label={getSectionName(section.section)}
          isActive={(match, location) => checkMenuActive(match, location, section, sectionSelected, sections)}
        >
          {sectionIcon}
        </NavLink>
      )}
      {hasChildren && isOpen && (
        <SubMenuPanel
          panelRef={panelRef}
          hasMoreBelow={hasMoreBelow}
          items={section.children}
          changeSection={changeSection}
          getIconSection={getIconSection}
          style={panelStyle}
          isTouchInput={isTouchInput}
          onNavigate={closePanel}
        />
      )}
    </div>
  );
};

const MenuSectionsComponent = ({ sections, sectionSelected, changeSection, getIconSection, reloadIconFlag }) => {
  // En tablet el menú se usa a dedo: los submenús no pueden depender del hover
  const isTouchInput = useIsTouchInput();

  useEffect(() => {}, [reloadIconFlag]);

  return (
    <div>
      {!!sections
        ? sections.map((section, index) => (
            <MenuSectionItem
              key={`${section?.section.name}-${index}`}
              section={section}
              changeSection={changeSection}
              getIconSection={getIconSection}
              sections={sections}
              sectionSelected={sectionSelected}
              isTouchInput={isTouchInput}
            />
          ))
        : ""}
    </div>
  );
};

export default MenuSectionsComponent;
